import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import AppDialog from '@/shared/components/ui/AppDialog';
import MinimalisticTable, { type ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import { createRoom } from '../api/rooms';
import { listLocks, type Lock } from '../api/locks';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: () => void | Promise<void>;

  /** Optional: pass locks from parent; if omitted, this dialog will fetch them. */
  locks?: Lock[];
  locksLoading?: boolean;
  locksError?: unknown;
};

export default function RoomCreateDialog({
  open,
  onOpenChange,
  onCreated,
  locks: locksProp,
  locksLoading: locksLoadingProp,
  locksError: locksErrorProp,
}: Props) {
  const [name, setName] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [selectedLockId, setSelectedLockId] = React.useState<number | null>(null);

  // If parent provided locks, use them; otherwise fetch here.
  const hasExternalLocks = Array.isArray(locksProp);
  const {
    data: locksQ = [],
    isLoading: locksLoadingQ,
    error: locksErrorQ,
  } = useQuery<Lock[]>({
    queryKey : ['ttlock', 'locks', 'for-room-select', 1, 100],
    queryFn  : () => listLocks(1, 100),
    staleTime: 5 * 60_000,
    enabled  : !hasExternalLocks,
  });

  const locks       = hasExternalLocks ? (locksProp ?? []) : locksQ;
  const locksLoading= hasExternalLocks ? !!locksLoadingProp : locksLoadingQ;
  const locksError  = hasExternalLocks ?  (locksErrorProp ?? null) : (locksErrorQ ?? null);

  const selectedLock = React.useMemo(
    () => locks.find(l => l.lockId === selectedLockId) ?? null,
    [locks, selectedLockId]
  );

  const columns = React.useMemo<ColumnDef<Lock>[]>(() => ([
    {
      header: <span className="text-neutral-300">Name</span>,
      accessor: 'lockAlias',
      Cell: (v: string | null) => (
        <span className="text-neutral-300">{v ?? '—'}</span>
      ),
    },
    {
      header: <span className="text-neutral-300">ID</span>,
      accessor: 'lockName',
      Cell: (v: string) => (
        <span className="text-neutral-300">{v}</span>
      ),
    },
    {
      header: <span className="text-neutral-300">Select</span>,
      accessor: '',
      Cell: (_v, row) => (
        <button
          className={
            selectedLockId === row.lockId
              ? 'px-3 py-1 rounded bg-emerald-700 text-white'
              : 'px-3 py-1 rounded bg-neutral-700 hover:bg-neutral-600 text-neutral-200'
          }
          onClick={(e) => { e.stopPropagation(); setSelectedLockId(row.lockId); }}
        >
          {selectedLockId === row.lockId ? 'Selected' : 'Select'}
        </button>
      ),
    },
  ]), [selectedLockId]);

  const canSubmit =
    name.trim().length > 0 &&
    selectedLockId !== null &&
    !locksLoading &&
    !locksError;

  const createFlow = useMutation({
    mutationFn: async () => {
      if (selectedLockId == null) throw new Error('Please select a lock.');
      return createRoom({
        name  : name.trim(),
        lockId: selectedLockId,
        notes : notes.trim() || null,
      });
    },
    onSuccess: async () => {
      setName('');
      setNotes('');
      setSelectedLockId(null);
      onOpenChange(false);
      await onCreated?.();
    },
  });

  return (
    <AppDialog
      open={open}
      onOpenChange={(v) => {
        if (!v && !createFlow.isPending) onOpenChange(v);
      }}
      title="Create room"
      description="Select a lock (required), enter the room name, and create the room."
      confirmLabel={createFlow.isPending ? 'Creating…' : 'Create room'}
      confirmDisabled={!canSubmit || createFlow.isPending}
      onConfirm={() => createFlow.mutate()}
      cancelLabel="Cancel"
      isBusy={createFlow.isPending}
      lockOnBusy
      size="lg"
    >
      <div className="space-y-4">
        <label className="block text-sm text-neutral-300">
          Name
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. 302 · Ocean View"
            className="mt-1 w-full bg-neutral-800 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </label>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-neutral-300">Select Lock (required)</span>
            <span className="text-xs text-neutral-400">
              {selectedLock
                ? `Selected: ${selectedLock.lockAlias ?? selectedLock.lockName} (#${selectedLock.lockId})`
                : 'None selected'}
            </span>
          </div>

          {locksLoading && <p className="text-neutral-400">Loading locks…</p>}
          {locksError && <p className="text-rose-400">Failed to load locks.</p>}

          {!locksLoading && !locksError && (
            <MinimalisticTable<Lock>
              columns={columns}
              data={locks}
              noDataMessage="No locks found."
              showNewButton={false}
              showSearch
              searchPlaceholder="Filter locks…"
              isRowClickable={() => false}
            />
          )}
        </div>

        <label className="block text-sm text-neutral-300">
          Notes (optional)
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything useful for staff…"
            className="mt-1 w-full bg-neutral-800 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </label>

        {createFlow.isError && (
          <p className="text-sm text-rose-400">
            {(createFlow.error as Error)?.message ?? 'Failed to create room.'}
          </p>
        )}
      </div>
    </AppDialog>
  );
}
